import React, { useMemo, useState, useEffect } from 'react';
import { useCurrency } from '../CurrencyContext';
import './WidgetsSection.css';

/**
 * Helper to get all dates in a range
 */
function getDatesInRange(startDate, endDate) {
  const dates = [];
  let curr = new Date(startDate);
  const end = new Date(endDate);
  
  let count = 0;
  while (curr <= end && count < 40) {
    const y = curr.getFullYear();
    const m = String(curr.getMonth() + 1).padStart(2, '0');
    const d = String(curr.getDate()).padStart(2, '0');
    dates.push(`${y}-${m}-${d}`);
    
    curr.setDate(curr.getDate() + 1);
    count++;
  }
  return dates;
}

// Local YYYY-MM-DD (avoids the UTC shift of toISOString()).
function fmtLocalDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

// Inclusive list of YYYY-MM-DD strings from `start` to `end`.
function enumerateDays(start, end) {
  const out = [];
  if (!start || !end) return out;
  let c = new Date(start + 'T00:00:00');
  const last = new Date(end + 'T00:00:00');
  let guard = 0;
  while (c <= last && guard < 400) {
    out.push(fmtLocalDate(c));
    c.setDate(c.getDate() + 1);
    guard++;
  }
  return out;
}

const prettyDay = (ds) =>
  new Date(ds + 'T00:00:00').toLocaleDateString('en-US', { day: 'numeric', month: 'short' });

/**
 * Cumulative ("incremental") spending across the current billing cycle.
 * The cycle's start/end come from the user's billing-cycle setting (passed
 * down as `from`/`to`), so e.g. a "last working day" cycle starting 29 May
 * begins the line on 29 May — not the calendar 1st. Each point is the
 * running total of spend from the cycle start through that day.
 */
function MonthlyCumulativeChart({ transactions, classifyForSpending, sym, from, to }) {
  const todayStr = fmtLocalDate(new Date());

  // Full cycle span (drives the X scale so the line shows progress through
  // the cycle) and the portion actually elapsed (we don't draw future days).
  const cycleDays = useMemo(() => enumerateDays(from, to), [from, to]);
  const plotEnd = to && todayStr > to ? to : todayStr;

  const points = useMemo(() => {
    if (!cycleDays.length) return [];
    const indexOf = new Map(cycleDays.map((ds, i) => [ds, i]));
    const perDay = {};
    cycleDays.forEach(ds => { perDay[ds] = 0; });
    transactions.forEach(tx => {
      const ds = tx.transaction_date?.slice(0, 10);
      if (ds === undefined || perDay[ds] === undefined) return;
      const cls = classifyForSpending
        ? classifyForSpending(tx)
        : (tx.transaction_type === 'debit' ? 'spend' : 'income');
      if (cls === 'spend') perDay[ds] += parseFloat(tx.amount || 0);
    });
    const out = [];
    let run = 0;
    for (const ds of cycleDays) {
      if (ds > plotEnd) break; // stop at today — don't plot future cycle days
      run += perDay[ds];
      out.push({ date: ds, idx: indexOf.get(ds), amount: run });
    }
    return out;
  }, [transactions, classifyForSpending, cycleDays, plotEnd]);

  // Previous cycle — an equal-length window ending the day before this
  // cycle starts. Plotted in full (it's entirely in the past) and aligned
  // by day-index so it overlays cleanly as a faint comparison line.
  const prevDays = useMemo(() => {
    if (!cycleDays.length) return [];
    const start = new Date(cycleDays[0] + 'T00:00:00');
    const prevTo = new Date(start); prevTo.setDate(prevTo.getDate() - 1);
    const prevFrom = new Date(prevTo); prevFrom.setDate(prevFrom.getDate() - (cycleDays.length - 1));
    return enumerateDays(fmtLocalDate(prevFrom), fmtLocalDate(prevTo));
  }, [cycleDays]);

  const prevPoints = useMemo(() => {
    if (!prevDays.length) return [];
    const perDay = {};
    prevDays.forEach(ds => { perDay[ds] = 0; });
    transactions.forEach(tx => {
      const ds = tx.transaction_date?.slice(0, 10);
      if (ds === undefined || perDay[ds] === undefined) return;
      const cls = classifyForSpending
        ? classifyForSpending(tx)
        : (tx.transaction_type === 'debit' ? 'spend' : 'income');
      if (cls === 'spend') perDay[ds] += parseFloat(tx.amount || 0);
    });
    const out = [];
    let run = 0;
    prevDays.forEach((ds, i) => { run += perDay[ds]; out.push({ date: ds, idx: i, amount: run }); });
    return out;
  }, [transactions, classifyForSpending, prevDays]);

  const total = points.length ? points[points.length - 1].amount : 0;
  const prevTotal = prevPoints.length ? prevPoints[prevPoints.length - 1].amount : 0;
  const maxAmt = Math.max(total, prevTotal, 1);
  const span = cycleDays.length;
  const rangeLabel = span ? `${prettyDay(cycleDays[0])} – ${prettyDay(cycleDays[span - 1])}` : '';

  // Coordinate space (viewBox units). The SVG scales to the container via
  // width:100%; non-scaling strokes keep line weight constant. Percentages
  // for the overlay dots map onto the same 0–100 box. topPad/bottomPad keep
  // the curve (and a flat zero-spend line) clear of the chart edges so it
  // stays visible instead of hugging the axis.
  const W = 100, H = 100, topPad = 10, bottomPad = 8;
  const baseY = H - bottomPad;
  const xFor = (idx) => span > 1 ? (idx / (span - 1)) * W : 0;
  const yFor = (amt) => baseY - (amt / maxAmt) * (baseY - topPad);

  const buildLine = (pts) => pts
    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xFor(p.idx).toFixed(2)} ${yFor(p.amount).toFixed(2)}`)
    .join(' ');
  const linePath = buildLine(points);
  const prevLinePath = buildLine(prevPoints);
  const areaPath = points.length
    ? `${linePath} L ${xFor(points[points.length - 1].idx).toFixed(2)} ${baseY} L ${xFor(points[0].idx).toFixed(2)} ${baseY} Z`
    : '';

  // A handful of evenly-spaced date labels for the X axis so the bottom
  // shows context without looking crowded.
  const labelTicks = useMemo(() => {
    if (span <= 1) return span === 1 ? [0] : [];
    const target = 5;
    const step = Math.max(1, Math.round((span - 1) / (target - 1)));
    const ticks = [];
    for (let i = 0; i < span; i += step) ticks.push(i);
    if (ticks[ticks.length - 1] !== span - 1) ticks.push(span - 1);
    return ticks;
  }, [span]);

  return (
    <div className="ws-widget ws-line-card">
      <div className="ws-header">
        <div className="ws-title-group">
          <h2 className="ws-title">Monthly Spending Trend</h2>
          <p className="ws-sub">Cumulative · {rangeLabel}</p>
        </div>
        <div className="ws-stats-row">
          <div className="ws-stat-pill">
            <span className="ws-stat-lbl">This cycle</span>
            <span className="ws-stat-val">{sym}{Math.round(total).toLocaleString()}</span>
          </div>
        </div>
      </div>

      <div className="ws-line-legend">
        <span className="ws-legend-item"><span className="ws-legend-dot" style={{ background: 'var(--accent-color)' }} /> This cycle</span>
        {prevPoints.length > 0 && (
          <span className="ws-legend-item ws-legend-prev"><span className="ws-legend-dot ws-legend-dot-prev" /> Previous</span>
        )}
      </div>

      <div className="ws-line-body">
        <div className="ws-line-plot">
          <svg
            className="ws-line-svg"
            viewBox={`0 0 ${W} ${H}`}
            preserveAspectRatio="none"
          >
            <defs>
              <linearGradient id="wsLineFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="var(--accent-color)" stopOpacity="0.35" />
                <stop offset="100%" stopColor="var(--accent-color)" stopOpacity="0" />
              </linearGradient>
            </defs>
            {/* Vertical gridlines under each date tick */}
            {labelTicks.map((i) => (
              <line
                key={`g${i}`}
                x1={xFor(i)} y1={topPad} x2={xFor(i)} y2={baseY}
                stroke="rgba(255,255,255,0.07)" strokeWidth="1"
                vectorEffect="non-scaling-stroke"
              />
            ))}
            {points.length > 0 && <path d={areaPath} fill="url(#wsLineFill)" />}
            {prevPoints.length > 0 && (
              <path
                d={prevLinePath}
                fill="none"
                stroke="rgba(255,255,255,0.35)"
                strokeWidth="1.75"
                strokeDasharray="3 3"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            )}
            {points.length > 0 && (
              <path
                d={linePath}
                fill="none"
                stroke="var(--accent-color)"
                strokeWidth="2.5"
                strokeLinecap="round"
                strokeLinejoin="round"
                vectorEffect="non-scaling-stroke"
              />
            )}
          </svg>
          <div className="ws-line-overlay">
            {points.map((p) => (
              <div
                key={p.date}
                className={`ws-line-dot-wrap ${p.date === todayStr ? 'is-today' : ''}`}
                style={{ left: `${xFor(p.idx)}%`, top: `${yFor(p.amount)}%` }}
              >
                <div className="ws-line-dot" />
                <div className="ws-bar-tooltip ws-line-tooltip">
                  <div className="ws-tt-date">{prettyDay(p.date)}</div>
                  <div className="ws-tt-amt">{sym}{Math.round(p.amount).toLocaleString()}</div>
                </div>
              </div>
            ))}
          </div>
          {points.length === 0 && prevPoints.length === 0 && (
            <div className="ws-line-empty">No spending recorded yet.</div>
          )}
        </div>
        <div className="ws-line-xaxis">
          {labelTicks.map((i) => (
            <span key={i} className="ws-line-xtick" style={{ left: `${xFor(i)}%` }}>
              {prettyDay(cycleDays[i])}
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function WidgetsSection({ transactions, from, to, classifyForSpending, onShowBreakdown, budget, onSetBudget }) {
  const { currency } = useCurrency();
  const sym = currency.symbol;
  const fmt = (n) => Math.round(n || 0).toLocaleString();

  const [range, setRange] = useState('7days');
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const { effectiveFrom, effectiveTo } = useMemo(() => {
    if (range === '7days') {
      const today = new Date();
      const start = new Date();
      start.setDate(today.getDate() - 6);
      return {
        effectiveFrom: start.toISOString().split('T')[0],
        effectiveTo: today.toISOString().split('T')[0]
      };
    }
    return { effectiveFrom: from, effectiveTo: to };
  }, [range, from, to]);

  // 1. Group transactions by day
  const dailyData = useMemo(() => {
    if (!effectiveFrom || !effectiveTo) return [];
    
    const dateRange = getDatesInRange(effectiveFrom, effectiveTo);
    const spendingMap = {};
    
    // Initialize map
    dateRange.forEach(d => spendingMap[d] = 0);
    
    // Sum debits (using the unified classifier)
    transactions.forEach(tx => {
      const dateKey = tx.transaction_date?.slice(0, 10);
      if (spendingMap[dateKey] === undefined) return;
      
      const cls = classifyForSpending ? classifyForSpending(tx) : (tx.transaction_type === 'debit' ? 'spend' : 'income');
      if (cls === 'spend') {
        spendingMap[dateKey] += parseFloat(tx.amount || 0);
      }
    });
    
    return dateRange.map(date => ({
      date,
      label: new Date(date).getDate(),
      dayName: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
      amount: spendingMap[date]
    }));
  }, [transactions, effectiveFrom, effectiveTo, classifyForSpending]);

  const maxAmount = useMemo(() => {
    const max = Math.max(...dailyData.map(d => d.amount), 0);
    return max === 0 ? 100 : max; // fallback for empty chart
  }, [dailyData]);

  const totalSpent = useMemo(() => {
    return dailyData.reduce((acc, d) => acc + d.amount, 0);
  }, [dailyData]);

  // ── Budget pace ──────────────────────────────────────────────
  // Tracks the monthly budget target against spend over the current
  // billing cycle [from, to]. The headline number is how much the user
  // can spend, on average, on each remaining day to stay within budget.
  // Set the budget = ₹60,000 over a 30-day cycle ⇒ ₹2,000/day to start;
  // overspend on any day and the remaining-day average drops accordingly.
  const budgetPace = useMemo(() => {
    const budgetNum = parseFloat(budget) || 0;
    if (!from || !to || budgetNum <= 0) {
      return { valid: false, budgetNum };
    }
    const MS = 86400000;
    const fromD = new Date(from + 'T00:00:00');
    const toD = new Date(to + 'T00:00:00');
    const today = new Date(); today.setHours(0, 0, 0, 0);

    const totalDays = Math.max(1, Math.round((toD - fromD) / MS) + 1);

    // Spend classified over the billing cycle.
    let spent = 0;
    transactions.forEach(tx => {
      const k = tx.transaction_date?.slice(0, 10);
      if (!k || k < from || k > to) return;
      const cls = classifyForSpending
        ? classifyForSpending(tx)
        : (tx.transaction_type === 'debit' ? 'spend' : 'income');
      if (cls === 'spend') spent += parseFloat(tx.amount || 0);
    });

    const idealDaily = budgetNum / totalDays;          // starting allowance
    const remaining = budgetNum - spent;               // reserve left

    let daysRemaining;
    if (today < fromD) daysRemaining = totalDays;      // cycle hasn't started
    else if (today > toD) daysRemaining = 0;           // cycle is over
    else daysRemaining = Math.round((toD - today) / MS) + 1; // incl. today

    const avgPerRemaining = daysRemaining > 0 ? remaining / daysRemaining : 0;
    const overBudget = remaining < 0;
    const onTrack = !overBudget && avgPerRemaining >= idealDaily;

    return {
      valid: true, budgetNum, totalDays, spent, idealDaily,
      remaining, daysRemaining, avgPerRemaining, overBudget, onTrack,
    };
  }, [budget, from, to, transactions, classifyForSpending]);

  // Position of the per-day budget line on the bar chart (% from bottom).
  const idealLinePct = budgetPace.valid
    ? Math.min((budgetPace.idealDaily / maxAmount) * 100, 100)
    : null;


  return (
    <div className="ws-root">
      <div className="ws-widgets-grid">

        {/* Cumulative monthly spending line chart (full width) */}
        <MonthlyCumulativeChart
          transactions={transactions}
          classifyForSpending={classifyForSpending}
          sym={sym}
          from={from}
          to={to}
        />

        {/* Main Chart Widget */}
        <div className="ws-widget ws-chart-card">
          <div className="ws-header">
            <div className="ws-title-group">
              <h2 className="ws-title">Spending Insights</h2>
              <p className="ws-sub">{effectiveFrom} to {effectiveTo}</p>
            </div>
            <div className="ws-filter-group">
              <button 
                className={`ws-filter-btn ${range === '7days' ? 'active' : ''}`}
                onClick={() => setRange('7days')}
              >
                Last 7 Days
              </button>
              <button 
                className={`ws-filter-btn ${range === 'month' ? 'active' : ''}`}
                onClick={() => setRange('month')}
              >
                Monthly
              </button>
            </div>
            <div className="ws-stats-row">
              <div
                className="ws-stat-pill clickable"
                onClick={onShowBreakdown}
                title="Click to see breakdown of this total"
              >
                <span className="ws-stat-lbl">Total Spent <span className="ws-stat-arrow">▾</span></span>
                <span className="ws-stat-val">{sym}{totalSpent.toLocaleString()}</span>
              </div>
            </div>
          </div>

          {/* ── Budget pace ── how much you can spend per remaining day ── */}
          {budgetPace.valid ? (
            <div className={`ws-budget ${budgetPace.overBudget ? 'over' : budgetPace.onTrack ? 'good' : 'tight'}`}>
              <div className="ws-budget-headline">
                {budgetPace.overBudget ? (
                  <>
                    <span className="ws-budget-amt">{sym}{fmt(Math.abs(budgetPace.remaining))} over</span>
                    <span className="ws-budget-note">You've used your full {sym}{fmt(budgetPace.budgetNum)} budget for this cycle.</span>
                  </>
                ) : budgetPace.daysRemaining <= 0 ? (
                  <>
                    <span className="ws-budget-amt">{sym}{fmt(budgetPace.remaining)} unspent</span>
                    <span className="ws-budget-note">Cycle complete — you stayed within budget.</span>
                  </>
                ) : (
                  <>
                    <span className="ws-budget-amt">{sym}{fmt(budgetPace.avgPerRemaining)}<span className="ws-budget-per">/day</span></span>
                    <span className="ws-budget-note">
                      You can spend this much on each of the next {budgetPace.daysRemaining} day{budgetPace.daysRemaining === 1 ? '' : 's'} and stay on budget
                      {' · '}started at {sym}{fmt(budgetPace.idealDaily)}/day
                    </span>
                  </>
                )}
              </div>
              <div className="ws-budget-meta">
                <div className="ws-budget-stat">
                  <span className="ws-budget-stat-lbl">Budget</span>
                  <span className="ws-budget-stat-val">{sym}{fmt(budgetPace.budgetNum)}</span>
                </div>
                <div className="ws-budget-stat">
                  <span className="ws-budget-stat-lbl">Spent</span>
                  <span className="ws-budget-stat-val spent">{sym}{fmt(budgetPace.spent)}</span>
                </div>
                <div className="ws-budget-stat">
                  <span className="ws-budget-stat-lbl">Reserve left</span>
                  <span className={`ws-budget-stat-val ${budgetPace.overBudget ? 'over' : 'left'}`}>{sym}{fmt(budgetPace.remaining)}</span>
                </div>
                {onSetBudget && (
                  <button className="ws-budget-edit" onClick={onSetBudget}>Edit</button>
                )}
              </div>
            </div>
          ) : (
            <button className="ws-budget-set" onClick={onSetBudget}>
              <span className="ws-budget-set-icon">🎯</span>
              <span>
                <b>Set your monthly budget</b>
                <span className="ws-budget-set-sub">Track how much you can spend each remaining day</span>
              </span>
            </button>
          )}

          <div className="ws-chart-body">
            <div className="ws-chart-container">
              {/* Per-day budget line — bars rising above it overspent that day */}
              {idealLinePct !== null && idealLinePct <= 100 && (
                <div className="ws-budget-line" style={{ bottom: `${idealLinePct}%` }}>
                  <span className="ws-budget-line-tag">{sym}{fmt(budgetPace.idealDaily)}/day</span>
                </div>
              )}
              {dailyData.map((day, idx) => {
                const heightPercent = (day.amount / maxAmount) * 100;
                const isToday = day.date === new Date().toISOString().split('T')[0];
                const overDay = budgetPace.valid && day.amount > budgetPace.idealDaily;

                // On small widgets (even desktop), show fewer labels
                const showLabel = (dailyData.length <= 10) || (idx % 5 === 0) || (idx === dailyData.length - 1);

                return (
                  <div key={day.date} className="ws-bar-wrapper" style={{ '--delay': `${idx * 0.01}s` }}>
                    <div className="ws-bar-tooltip">
                      <div className="ws-tt-date">{day.dayName}, {day.date}</div>
                      <div className="ws-tt-amt">{sym}{day.amount.toLocaleString()}</div>
                    </div>
                    <div
                      className={`ws-bar ${isToday ? 'is-today' : ''} ${overDay ? 'over-budget' : ''}`}
                      style={{ height: `${Math.max(heightPercent, 3)}%` }}
                    >
                    </div>
                    {showLabel && (
                      <div className="ws-bar-x-label">
                        <span className="ws-x-date">{day.label}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Placeholder Widget 2 */}
        <div className="ws-widget">
          <div className="ws-header">
            <div className="ws-title-group">
              <h2 className="ws-title">Category Split</h2>
              <p className="ws-sub">Coming Soon</p>
            </div>
          </div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: 0.2 }}>
            <span style={{ fontSize: '3rem' }}>🥧</span>
          </div>
        </div>

      </div>
    </div>
  );
}
